import React, {useCallback, useMemo, useState} from 'react'
import {View, momentLocalizer} from 'react-big-calendar'
import moment from 'moment'
import {Typography, useMediaQuery, useTheme} from '@mui/material'
import ActivityDetailDialogNew from 'src/components-new/feedback/ActivityDetailDialogNew'
import {CalendarStyledNew} from 'src/pages-new/agenda/CalendarStyledNew'
import CalendarToolbarNew from 'src/pages-new/agenda/CalendarToolbarNew'
import {isItalianHoliday} from 'src/pages-new/agenda/agendaUtils'
import {SEMANTIC} from 'src/theme-new/tokens'
import {getBeneficiaryName, PatientActivityRow} from './patientActivitiesUtils'

// ---------------------------------------------------------------------------
// Module-level localizer (stable reference — must not be created inside render)
// ---------------------------------------------------------------------------

const localizer = momentLocalizer(moment)

// ---------------------------------------------------------------------------
// Sub-renderers (defined outside the component to avoid recreating on every render)
// ---------------------------------------------------------------------------

// Cancelled activities get a distinct chip color + strikethrough label so they read as
// cancelled at a glance on the calendar, without opening the detail dialog to find out.
const isCancelled = (event: PatientActivityRow) => event.state === 'CANCELLED'

const cancelledChipStyle = (event: PatientActivityRow) =>
  isCancelled(event) ? {backgroundColor: SEMANTIC.error.main} : undefined

const cancelledLabelSx = (event: PatientActivityRow) =>
  isCancelled(event) ? {textDecoration: 'line-through', color: SEMANTIC.error.dark} : undefined

// Month/week/day cell — mirrors the legacy ActivitiesAgenda EventBox label
// ("Cognome Nome - Titolo") but with the new-style chip/time classNames
// already styled by CalendarStyledNew.
const EventBox = ({event}: {event: PatientActivityRow}) => (
  <div className="event-box">
    <div>
      <div className="event-chip" style={cancelledChipStyle(event)} />
      <Typography sx={{fontSize: '11px', fontWeight: 600, ...cancelledLabelSx(event)}}>
        {moment(event.startDate).format('HH:mm')}
      </Typography>
    </div>
    <Typography className="event-reason" variant="body2" sx={cancelledLabelSx(event)}>
      {`${getBeneficiaryName(event)} - ${event.title ?? ''}`}
    </Typography>
  </div>
)

// Agenda/Lista view row — same label as EventBox, laid out with the
// time-label/event-chip/title-label classNames CalendarStyledNew styles for
// the agenda table specifically.
const AgendaEventBox = ({event}: {event: PatientActivityRow}) => (
  <div className="event-box">
    <Typography className="event-reason" variant="body2" sx={cancelledLabelSx(event)}>
      <span className="time-label">{moment(event.startDate).format('HH:mm')}</span>
      <span className="event-chip" style={cancelledChipStyle(event)} />
      <span className="title-label">{`${getBeneficiaryName(event)} - ${event.title ?? ''}`}</span>
    </Typography>
  </div>
)

const DateBox = ({day}: {day: Date}) => (
  <div className="date-box">
    <Typography className="event-reason" variant="body2">{moment(day).format('ddd')}</Typography>
    <Typography className="event-reason" variant="body2">{moment(day).format('DD MMMM YYYY')}</Typography>
  </div>
)

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PatientActivitiesCalendarNewProps {
  events: PatientActivityRow[]
  // moment objects, like the legacy page and AgendaNew — `getPatientsActivities`
  // only needs `.toISOString()`, which both moment and Date provide.
  startDate: moment.Moment
  setStartDate: (date: moment.Moment) => void
  endDate: moment.Moment
  setEndDate: (date: moment.Moment) => void
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Read-only calendar for GET psp-backend/patients-activities/, replacing the
 * legacy ActivitiesAgenda (src/components/templates/ActivitiesAgenda) with the
 * new-style calendar building blocks (CalendarStyledNew + CalendarToolbarNew,
 * already used by AgendaNew/CalendarNew). No create/edit/delete action, no
 * `selectable`/`onSelectSlot` — activities are only ever viewed, never created
 * here (mirrors the legacy page's read-only behavior).
 */
export default function PatientActivitiesCalendarNew({
  events,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}: PatientActivitiesCalendarNewProps): JSX.Element {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Defaults to 'agenda' (Lista) to match the legacy page's forced
  // `defaultView='agenda'`, but — unlike the legacy hardcap — the toolbar's
  // view selector stays fully enabled (Giorno/Settimana/Mese/Lista).
  const [calendarView, setCalendarView] = useState<View>('agenda')
  const [calendarDate, setCalendarDate] = useState<Date>(new Date())
  const [selectedActivity, setSelectedActivity] = useState<PatientActivityRow | null>(null)

  // -------------------------------------------------------------------------
  // Navigation handler — identical logic to the legacy
  // ActivitiesAgenda.handleDateChange: if the clicked date falls outside the
  // current [startDate, endDate] range, jump start/end to the month
  // containing it (triggering the parent's refetch); always update the
  // calendar's own display cursor.
  // -------------------------------------------------------------------------

  const handleDateChange = useCallback((date: Date) => {
    const isoDate = moment(date).toISOString()
    if (!(moment(startDate).toISOString() <= isoDate && isoDate <= moment(endDate).toISOString())) {
      setStartDate(moment(date).startOf('month'))
      setEndDate(moment(date).endOf('month'))
    }
    setCalendarDate(new Date(date)) // clone to avoid mutation
  }, [startDate, endDate, setStartDate, setEndDate])

  // -------------------------------------------------------------------------
  // Holiday day prop getter — same Sunday/holiday tint as CalendarNew
  // (Saturday stays a regular weekday, per client request there).
  // -------------------------------------------------------------------------

  const dayPropGetter = useCallback((date: Date) => {
    if (date.getDay() === 0 || isItalianHoliday(date)) {
      return {className: 'rbc-holiday-day'}
    }
    return {}
  }, [])

  // -------------------------------------------------------------------------
  // Calendar components config — memoized so react-big-calendar does not
  // remount toolbar/event renderers on unrelated state changes.
  // -------------------------------------------------------------------------

  const calendarComponents = useMemo(() => ({
    toolbar: () => (
      <CalendarToolbarNew
        date={calendarDate}
        view={calendarView}
        handleDateChange={handleDateChange}
        handleViewChange={setCalendarView}
        isMobile={isMobile}
      />
    ),
    // Typed `any` here (not PatientActivityRow/Date) to match react-big-calendar's own
    // Components<TEvent, TResource> shape, same convention already used by CalendarNew.tsx.
    week: {event: ({event}: any) => <EventBox event={event} />},
    day: {event: ({event}: any) => <EventBox event={event} />},
    month: {event: ({event}: any) => <EventBox event={event} />},
    agenda: {
      event: ({event}: any) => <AgendaEventBox event={event} />,
      date: ({day}: any) => <DateBox day={day} />,
    },
  // setCalendarView is a stable useState setter — intentionally omitted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [calendarDate, calendarView, handleDateChange, isMobile])

  return (
    <>
      <CalendarStyledNew
        localizer={localizer}
        culture="it"
        events={events}
        view={calendarView}
        date={calendarDate}
        dayLayoutAlgorithm="no-overlap"
        startAccessor={(event: any) => new Date(event.startDate)}
        endAccessor={(event: any) => new Date(event.endDate)}
        onNavigate={handleDateChange}
        onView={setCalendarView}
        onSelectEvent={(event: any) => setSelectedActivity(event)}
        dayPropGetter={dayPropGetter}
        style={{height: isMobile ? 'calc(100vh - 260px)' : 'calc(100vh - 180px)'}}
        components={calendarComponents}
      />

      {selectedActivity && (
        <ActivityDetailDialogNew
          activity={selectedActivity}
          open={Boolean(selectedActivity)}
          onClose={() => setSelectedActivity(null)}
        />
      )}
    </>
  )
}
